// ============================================================
//  src/shack/thumbnails.js — Generación de miniaturas
//
//  Descarga archivos de Drive con fetch + Bearer token OAuth.
//  Devuelve blob URLs locales — sin CORS, sin popup de login.
// ============================================================

import { obtenerAccessToken }                    from '../drive.js';
import { functions }                             from '../firebase.js';
import { httpsCallable }                         from 'firebase/functions';
import { pdfBlobToImageBlobUrl }                 from './pdfUtils.js';

const TAMANO_DEFAULT = 800;
const CACHE = new Map(); // fileId → blob URL

/**
 * Descarga un archivo de Drive usando el token OAuth.
 * Devuelve una blob URL local que Three.js carga sin CORS.
 *
 * @param {string} fileId
 * @returns {Promise<string|null>} blob URL o null si falla
 */
export async function generarThumbnail(fileId, tamano = TAMANO_DEFAULT) {
    if (!fileId) return null;

    const cacheKey = `${fileId}_${tamano}`;
    if (CACHE.has(cacheKey)) return CACHE.get(cacheKey);

    const token = obtenerAccessToken();
    if (!token) {
        console.warn('[Thumbnail] Sin token OAuth — abrí Drive primero para autorizar.');
        return null;
    }

    try {
        const url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;

        const respuesta = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!respuesta.ok) {
            console.warn(`[Thumbnail] Error HTTP ${respuesta.status} para ${fileId}`);
            return null;
        }

        const blob = await respuesta.blob();
        const blobUrl = blob.type === 'application/pdf'
            ? await pdfBlobToImageBlobUrl(blob)
            : URL.createObjectURL(blob);

        if (!blobUrl) return null;
        CACHE.set(cacheKey, blobUrl);
        return blobUrl;

    } catch (err) {
        console.warn(`[Thumbnail] Error descargando ${fileId}:`, err.message);
        return null;
    }
}

// Proxy via Cloud Function — evita CORS de fetch() directo a drive.google.com/lh3.
// La función obtiene thumbnailLink de Drive API y lo devuelve como base64.
export async function generarThumbnailManagedDrive(fileId) {
    if (!fileId) return null;

    const cacheKey = `managed_${fileId}`;
    if (CACHE.has(cacheKey)) return CACHE.get(cacheKey);

    try {
        const fn = httpsCallable(functions, 'managedDriveImage');
        const { data } = await fn({ fileId });

        if (!data?.imageBase64) return null;

        const bytes   = Uint8Array.from(atob(data.imageBase64), (c) => c.charCodeAt(0));
        const blob    = new Blob([bytes], { type: data.mimeType || 'image/jpeg' });
        const blobUrl = blob.type === 'application/pdf'
            ? await pdfBlobToImageBlobUrl(blob)
            : URL.createObjectURL(blob);

        if (!blobUrl) return null;
        CACHE.set(cacheKey, blobUrl);
        return blobUrl;

    } catch (err) {
        console.warn(`[Thumbnail] managedDriveImage error fileId=${fileId}:`, err.message);
        return null;
    }
}

/**
 * Genera thumbnails para un array de certificados en lotes de 4.
 * Despacha por storageType: managed_drive no necesita token OAuth.
 *
 * @param {Array} certificados
 * @returns {Promise<Map>} Map<fileId, blobUrl>
 */
export async function generarThumbnailsEnLote(certificados) {
    const resultados = new Map();
    const LOTE = 4;

    for (let i = 0; i < certificados.length; i += LOTE) {
        const lote = certificados.slice(i, i + LOTE);

        await Promise.all(
            lote.map(async (cert) => {
                if (!cert.fileId) return;
                const url = cert.storageType === 'managed_drive'
                    ? await generarThumbnailManagedDrive(cert.fileId)
                    : await generarThumbnail(cert.fileId);
                if (url) resultados.set(cert.fileId, url);
            })
        );
    }

    return resultados;
}

/**
 * Libera las blob URLs para evitar memory leaks.
 * Llamar al cerrar el shack.
 */
export function limpiarCacheThumbnails() {
    for (const blobUrl of CACHE.values()) {
        URL.revokeObjectURL(blobUrl);
    }
    CACHE.clear();
    console.log('[Thumbnail] Cache limpiado.');
}
